import { NgClass } from '@angular/common';
import { Component, computed, inject, input, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ScheduleBaseItemComponent } from '@app/+authenticated/+schedule/employee/schedule-employee-n-period/schedule-items/base/schedule-base-item.component';
import { ScheduleItemTooltipTemplateDirective } from '@app/+authenticated/+schedule/employee/schedule-employee-n-period/schedule-items/base/schedule-item-tooltip-template.directive';
import { NPeriodService } from '@app/+authenticated/+schedule/shared/schedule-actions/n-period.service';
import { ScheduleNActionService } from '@app/+authenticated/+schedule/shared/schedule-actions/schedule-n-action.service';
import { ScheduleItemTooltipComponent } from '@app/+authenticated/+schedule/shared/schedule-items/schedule-item-tooltip.component';
import { FindCoverService } from '@app/+modals/schedule/schedule-find-cover/find-cover.service';
import { DragType } from '@app/enums';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { TimePipe } from '@app/pipes/time.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import type { ScheduleModel } from '@reducers/orm/schedule/schedule.model';
import { ScheduleService } from '@reducers/orm/schedule/schedule.service';
import type { ShiftModel } from '@reducers/orm/shift/shift.model';
import type { TeamModel } from '@reducers/orm/team/team.model';
import { mgcIconLockRegular } from '@sb/mgc-icons/mgcIcon-lock_regular.icon';
import { mgcIconRepeatRegular } from '@sb/mgc-icons/mgcIcon-repeat_regular.icon';
import { mgcIconTask2Regular } from '@sb/mgc-icons/mgcIcon-task_2_regular.icon';
import { mgcIconTransfer3Regular } from '@sb/mgc-icons/mgcIcon-transfer_3_regular.icon';
import { mgcIconWarningFilled } from '@sb/mgc-icons/mgcIcon-warning_filled.icon';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { SpinnerComponent } from '@sb/ui/spinner';
import type { PeriodType } from '@shared/interfaces';
import { PlanType } from '@shiftbase-com/ts-shared/models';

import { isScheduleDisabled } from '../is-schedule-disabled';
import { useScheduledShift } from '../use-scheduled-shift';

@Component({
  selector: 'schedule-shift-template',
  templateUrl: './schedule-shift-template.component.html',
  imports: [
    TimePipe,
    NgClass,
    PlanCheckPipe,
    IconComponent,
    SpinnerComponent,
    TranslatePipe,
    ScheduleBaseItemComponent,
    ScheduleItemTooltipComponent,
    ScheduleItemTooltipTemplateDirective,
  ],
})
export class ScheduleShiftTemplateComponent {
  #router = inject(Router);
  #zone = inject(NgZone);
  #scheduleActionService = inject(ScheduleNActionService);
  #periodService = inject(NPeriodService);
  #scheduleService = inject(ScheduleService);
  #findCover = inject(FindCoverService);

  schedule = input.required<ScheduleModel>();
  team = input<TeamModel>();
  overrideShift = input<ShiftModel>();
  overridePeriodType = input<PeriodType>();
  departmentId = input<string>();
  mode = input<'employee' | 'team'>('employee');
  disableHoverButtons = input(false);

  #scheduledShift = useScheduledShift(this.schedule);

  shift = computed(() => this.overrideShift() ?? this.#scheduledShift.shift());
  scheduledTeam = this.#scheduledShift.scheduledTeam;
  scheduledDepartment = this.#scheduledShift.scheduledDepartment;
  violation = this.#scheduledShift.violation;
  conflicts = this.#scheduledShift.conflicts;
  isProposed = this.#scheduledShift.isProposed;
  isLocked = this.#scheduledShift.isLocked;
  isConflicted = this.#scheduledShift.isConflicted;
  canFindCover = this.#scheduledShift.canFindCover;

  isDisabled = computed(() => isScheduleDisabled(this.schedule(), this.team(), this.departmentId()));
  periodType = computed(() => this.overridePeriodType() ?? this.#periodService.getPeriod().periodType);

  DragType = DragType;
  planType = PlanType;

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconWarningFilled,
      mgcIconTask2Regular,
      mgcIconTransfer3Regular,
      mgcIconRepeatRegular,
      mgcIconLockRegular,
    ]);
  }

  openFindCover(event: MouseEvent): void {
    event.stopPropagation();

    this.#findCover.open(this.schedule().occurrence_id);
  }

  deleteSchedule(): void {
    const schedule = this.schedule();

    this.#zone.run(() => {
      if (this.#scheduleActionService.isHoldingKey$.value) {
        this.#scheduleService
          .remove(schedule.occurrence_id, { notify: false }, schedule.recurring ? 'occurrence' : 'original', {
            userId: schedule.user_id,
            teamId: schedule.team_id,
            departmentId: schedule.department_id,
          })
          .subscribe();
        return;
      }

      this.#router.navigate(['', { outlets: { modal: ['schedule-shift', schedule.occurrence_id, 'delete'] } }]);
    });
  }
}
